import { useCallback, useEffect, useMemo, useState } from 'react';
import SiteLayout, { Stripe } from './SiteLayout';
import usePhotoAlbums from './usePhotoAlbums';
import PhotoSearch from './PhotoSearch';

// Every event's photo album, newest first, grouped by year. Each card opens
// the album's public share page in the NHCA photo archive (Immich), which
// does the browsing, zooming and downloading. Albums are made and kept up to
// date by backend/photo_sync.py; nothing here needs curating. The intro and
// the links under it (e.g. Instagram) are edited in Web Admin > Photos page.

const DEFAULT_INTRO = 'Every event in the archive, newest first. Each one opens in the NHCA photo archive, where you can browse, zoom in and download.';

function usePageFields() {
  const [fields, setFields] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/site/pages/photos')
      .then(res => (res.ok ? res.json() : null))
      .then(d => (d ? JSON.parse(d.body_md) : {}))
      .catch(() => ({}))
      .then(f => { if (!cancelled) setFields(f); });
    return () => { cancelled = true; };
  }, []);
  return fields;
}

function fmtDate(iso) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long' });
}

function CollectionCard({ collection }) {
  const body = (
    <>
      <span className="site-album-photo">
        {collection.cover ? <img src={collection.cover} alt="" loading="lazy" /> : null}
      </span>
      <span className="site-album-text">
        <span className="site-album-hill">{collection.title}</span>
        <span className="site-album-meta">{collection.count} photos</span>
        {collection.description && <span className="site-album-desc">{collection.description}</span>}
      </span>
    </>
  );
  return <a className="site-album" href={collection.url} target="_blank" rel="noreferrer">{body}</a>;
}

function EventCard({ event }) {
  const body = (
    <>
      <span className="site-album-photo">
        {event.cover ? <img src={event.cover} alt="" loading="lazy" /> : null}
      </span>
      <span className="site-album-text">
        <span className="site-album-hill">{event.venue || event.hill}</span>
        <span className="site-album-meta">{fmtDate(event.date)} · {event.count} {event.count === 1 ? 'photo' : 'photos'}</span>
      </span>
    </>
  );
  return event.url
    ? <a className="site-album" href={event.url} target="_blank" rel="noreferrer">{body}</a>
    : <div className="site-album">{body}</div>;
}

export default function SitePhotos() {
  const { events, collections = [] } = usePhotoAlbums();
  const [hill, setHill] = useState('');
  const [searching, setSearching] = useState(false);
  const onSearchChange = useCallback(active => setSearching(active), []);
  const fields = usePageFields();
  const pageLinks = (fields?.links || []).filter(l => l && l.label && l.href);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Photos · National Hill Climb Association';
    return () => { document.title = previous; };
  }, []);

  // Venue names, so "Gurston" and "Gurston Down" are one choice.
  const hills = useMemo(() => [...new Set(events.map(e => e.venue || e.hill))].sort(), [events]);
  const years = useMemo(() => {
    const byYear = new Map();
    for (const e of events) {
      if (hill && (e.venue || e.hill) !== hill) continue;
      if (!byYear.has(e.year)) byYear.set(e.year, []);
      byYear.get(e.year).push(e);
    }
    return [...byYear.entries()];
  }, [events, hill]);

  return (
    <SiteLayout>
      <section className="site-dark site-page-head">
        <div className="site-wrap site-section-head">
          <div>
            <span className="site-kicker">NHCA photo archive</span>
            <h1 className="site-h2">Photos</h1>
            <p>{fields ? (fields.intro || DEFAULT_INTRO) : '\u00a0'}</p>
            {pageLinks.length > 0 && (
              <div className="site-page-links">
                {pageLinks.map(l => (
                  <a key={l.href} href={l.href} target="_blank" rel="noreferrer">{l.label} →</a>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <Stripe />

      <section className="site-albums">
        <div className="site-wrap">
          <PhotoSearch onActiveChange={onSearchChange} />
          {searching ? null : events.length === 0 && collections.length === 0 ? (
            <p className="site-albums-empty">The photo archive is being put together. Check back soon.</p>
          ) : (
            <>
              <div className="site-albums-tools">
                <label>
                  <span>Hill</span>
                  <select value={hill} onChange={e => setHill(e.target.value)}>
                    <option value="">All hills</option>
                    {hills.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </label>
                {years.length + (collections.length > 0) > 1 && (
                  <label>
                    <span>Year</span>
                    <select value="" onChange={e => document.getElementById(e.target.value)?.scrollIntoView()}>
                      <option value="">Jump to…</option>
                      {collections.length > 0 && !hill && <option value="photos-collections">Collections</option>}
                      {years.map(([y]) => <option key={y} value={`photos-${y}`}>{y}</option>)}
                    </select>
                  </label>
                )}
              </div>
              {collections.length > 0 && !hill && (
                <section id="photos-collections" className="site-albums-year" aria-labelledby="photos-collections-title">
                  <h2 className="site-albums-year-title" id="photos-collections-title">Collections</h2>
                  <div className="site-album-grid">
                    {collections.map(c => <CollectionCard key={c.url} collection={c} />)}
                  </div>
                </section>
              )}
              {years.map(([y, list]) => (
                <section key={y} id={`photos-${y}`} className="site-albums-year" aria-labelledby={`photos-${y}-title`}>
                  <h2 className="site-albums-year-title" id={`photos-${y}-title`}>{y}</h2>
                  <div className="site-album-grid">
                    {list.map(e => <EventCard key={`${e.date}-${e.hill}`} event={e} />)}
                  </div>
                </section>
              ))}
            </>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
